import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MD_DIALOG_DATA, MdDialog, MdDialogRef } from "@angular/material";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs/Subscription";
import { PictureSet } from "../../models/pictureSets";
import { PictureSetService } from "../../services/pictureSet.service";
import { additionalPicture } from "../../models/additionalPicture";
import { Language } from "../../models/language";
import { User } from "../../models/user";
import { ProgramArticlePictureETTValue } from "../../models/program-article-translation-value";
import { SuccessComponent } from "../success/success.component";
import { EventArticlePictureRequest } from "../../models/eventArticlePictureRequest";
import { EventArticleService } from "../../services/event-article.service";
import { EventArticle } from "../../models/event-article";
import { EventArticlesStateInterface } from "../../store/actions/event-articles.actions";
import { eventAdditionalPictureRequest } from "../../models/eventAdditionalPictureRequest";
import { LanguageService } from "../../services/language.service";
import { finalize } from "rxjs/operators";

export class LanguagePicture {
  languageId: number;
  language: string;
  originalPicture: string;
  cropPicture: string;
  originalPictureId: number;
  cropPictureId: number;
  format: string;
  formatId: number;
}

@Component({
  selector: "app-event-max-multilingual-modal",
  templateUrl: "./event-max-multilingual-modal.component.html",
  styleUrls: ["./event-max-multilingual-modal.component.scss"],
})
export class EventMaxMultilingualModalComponent implements OnInit, OnDestroy {
  loading: boolean;
  eventArticle: EventArticle;
  subscriptions: Subscription[];
  pictureSet: PictureSet;
  selectedPictureSet: LanguagePicture;
  additionalPicture: additionalPicture[];
  initAdditionalPicture: additionalPicture[];
  languages: Language[];
  currentUser: User;
  pictureSetUpdated: boolean = false;
  checkBox: boolean = false;
  languageChecked = [];

  constructor(
    @Inject(MD_DIALOG_DATA) public data: any,
    public dialogRef: MdDialogRef<EventMaxMultilingualModalComponent>,
    private mdDialog: MdDialog,
    private store: Store<any>,
    private pictureSetService: PictureSetService,
    private eventArticleService: EventArticleService,
    private langageService: LanguageService
  ) {
    this.loading = true;
    this.subscriptions = [];
    this.languages = [];
  }

  ngOnInit() {
    this.selectedPictureSet = new LanguagePicture();
    this.getUserState();

    this.subscriptions.push(
      this.store
        .select("eventArticlesState")
        .subscribe((eventArticleState: EventArticlesStateInterface) => {
          this.loading = true;
          this.eventArticle = Object.assign(
            {},
            eventArticleState.currentEventArticle
          );
        })
    );

    this.langageService
      .getAllLanguages()
      .pipe(
        finalize(() => {
          this.pictureSet = this.pictureSetService.getPictureSet();

          this.getEventAdditionalsPicture();

          this.selectedPictureSet.languageId =
            this.currentUser.currentLanguage.id;
          this.selectedPictureSet.language =
            this.currentUser.currentLanguage.value;
          this.selectedPictureSet.originalPicture =
            this.pictureSet.pictures[0].pictureURL;
          this.selectedPictureSet.cropPicture =
            this.pictureSet.pictures[1].pictureURL;
          this.selectedPictureSet.originalPictureId =
            this.pictureSet.pictures[0].pictureId;
          this.selectedPictureSet.cropPictureId =
            this.pictureSet.pictures[1].pictureId;
          this.selectedPictureSet.format =
            this.pictureSet.pictures[1].pictureFormatCode;
          this.selectedPictureSet.formatId =
            this.pictureSet.pictures[1].pictureFormatId;

          this.loading = false;
        })
      )
      .subscribe((languages) => {
        this.languages = languages;
      });
  }

  getUserState() {
    this.subscriptions.push(
      this.store.select("userState").subscribe((user: User) => {
        if (user && user.currentLanguage) {
          this.currentUser = user;
        }
      })
    );
  }

  getEventAdditionalsPicture() {
    const request = new EventArticlePictureRequest({
      SportingEventId: this.eventArticle.id,
      SetId: this.pictureSet.setId,
      LanIds: this.languages.map((l) => l.id),
    });

    this.eventArticleService
      .getEventAdditionalsPicture(request)
      .subscribe((pictures) => {
        this.additionalPicture = pictures;

        this.languages.forEach((lang) => {
          const alreadyExists = this.additionalPicture.some(
            (p) => p.languageId === lang.id
          );
          if (!alreadyExists) {
            this.additionalPicture.push({
              languageId: lang.id,
              languageName: "",
              originalPictureId: 0,
              originalPictureURL: "",
              cropPictureId: 0,
              cropPictureURL: "",
              pictureFormatId: 0,
              checked: false,
              duplicate: false,
            });
          }
        });

        this.additionalPicture = this.additionalPicture.filter(
          (p) => p.languageId !== this.currentUser.currentLanguage.id
        );

        this.additionalPicture = this.additionalPicture.map((p) => {
          const matchingLanguage = this.languages.find(
            (lang) => lang.id === p.languageId
          );

          return {
            ...p,
            languageName: matchingLanguage.value,
          };
        });

        this.initAdditionalPicture = JSON.parse(
          JSON.stringify(this.additionalPicture)
        );
      });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getPictureSourceByFormat(
    pictures: ProgramArticlePictureETTValue[],
    formatName: string
  ) {
    return (
      this.getPictureByFormat(pictures, formatName).pictureURL ||
      this.getPictureByFormat(pictures, formatName).blob
    );
  }

  getPictureByFormat(
    pictures: ProgramArticlePictureETTValue[],
    formatName: string
  ) {
    if (pictures && Array.isArray(pictures))
      return pictures.find((ps) => ps.pictureFormatName === formatName);
    return null;
  }

  duplicatePicture() {
    this.additionalPicture.forEach((p) => {
      p.cropPictureId = this.pictureSet.pictures[1].pictureId;
      p.cropPictureURL = this.pictureSet.pictures[1].pictureURL;
      p.originalPictureId = this.pictureSet.pictures[0].pictureId;
      p.originalPictureURL = this.pictureSet.pictures[0].pictureURL;
      p.pictureFormatId = this.pictureSet.pictures[1].pictureFormatId;
    });
    this.pictureSetUpdated = true;
  }

  duplicateEmptyPicture(picture) {
    picture.checked = true;
    picture.duplicate = true;
    this.additionalPicture.forEach((p) => {
      if (p.languageId == picture.languageId) {
        p.cropPictureId = this.pictureSet.pictures[1].pictureId;
        p.cropPictureURL = this.pictureSet.pictures[1].pictureURL;
        p.originalPictureId = this.pictureSet.pictures[0].pictureId;
        p.originalPictureURL = this.pictureSet.pictures[0].pictureURL;
        p.pictureFormatId = this.pictureSet.pictures[1].pictureFormatId;
      }
    });
    this.pictureSetUpdated = true;
  }

  duplicateToEmpty() {
    this.additionalPicture.forEach((p) => {
      if (p.originalPictureId == 0 && p.cropPictureId == 0) {
        p.cropPictureId = this.pictureSet.pictures[1].pictureId;
        p.cropPictureURL = this.pictureSet.pictures[1].pictureURL;
        p.originalPictureId = this.pictureSet.pictures[0].pictureId;
        p.originalPictureURL = this.pictureSet.pictures[0].pictureURL;
        p.pictureFormatId = this.pictureSet.pictures[1].pictureFormatId;
      }
    });
    this.pictureSetUpdated = true;
  }

  copyPicture(duplicateType: number) {
    this.checkBox = true;
    this.pictureSetUpdated = false;
    if (duplicateType == 1) {
      this.additionalPicture.forEach((p) => {
        p.cropPictureId = this.pictureSet.pictures[1].pictureId;
        p.cropPictureURL = this.pictureSet.pictures[1].pictureURL;
        p.originalPictureId = this.pictureSet.pictures[0].pictureId;
        p.originalPictureURL = this.pictureSet.pictures[0].pictureURL;
        p.pictureFormatId = this.pictureSet.pictures[1].pictureFormatId;
        p.checked = true;
      });
    }

    if (duplicateType == 2) {
      this.additionalPicture.forEach((p) => {
        if (p.cropPictureId == 0) {
          p.cropPictureId = this.pictureSet.pictures[1].pictureId;
          p.cropPictureURL = this.pictureSet.pictures[1].pictureURL;
          p.originalPictureId = this.pictureSet.pictures[0].pictureId;
          p.originalPictureURL = this.pictureSet.pictures[0].pictureURL;
          p.pictureFormatId = this.pictureSet.pictures[1].pictureFormatId;
          p.checked = true;
        }
      });
    }

    if (duplicateType == 3) {
      this.additionalPicture.forEach((p) => {
        p.checked = false;
      });
    }
  }

  cancelDuplicatePicture() {
    this.checkBox = false;
    this.additionalPicture = JSON.parse(
      JSON.stringify(this.initAdditionalPicture)
    );
  }

  checkDuplicatePicture(picture, event) {
    if (event.checked == true) {
      this.additionalPicture.forEach((p) => {
        if (p.languageId == picture.languageId) {
          p.cropPictureId = this.pictureSet.pictures[1].pictureId;
          p.cropPictureURL = this.pictureSet.pictures[1].pictureURL;
          p.originalPictureId = this.pictureSet.pictures[0].pictureId;
          p.originalPictureURL = this.pictureSet.pictures[0].pictureURL;
          p.pictureFormatId = this.pictureSet.pictures[1].pictureFormatId;
        }
      });
    } else {
      this.additionalPicture.forEach((p) => {
        if (p.languageId == picture.languageId) {
          p.cropPictureId = 0;
          p.cropPictureURL = "";
          p.originalPictureId = 0;
          p.originalPictureURL = "";
          p.pictureFormatId = 0;
          p.checked = false;
        }
      });
    }
  }

  applyDuplicatePicture() {
    this.checkBox = false;
    this.pictureSetUpdated = true;
    this.additionalPicture.forEach((picture) => {
      picture.duplicate = false;
    });
    this.initAdditionalPicture = JSON.parse(
      JSON.stringify(this.additionalPicture)
    );
  }

  save() {
    this.pictureSetUpdated = false;
    this.loading = true;
    let additionalPictureToSave = this.additionalPicture.filter(
      (additionalPicture) =>
        additionalPicture.cropPictureId !== 0 &&
        additionalPicture.originalPictureId !== 0
    );
    const request = new eventAdditionalPictureRequest({
      sportingEventId: this.eventArticle.id,
      languageId: this.currentUser.currentLanguage.id,
      setId: this.pictureSet.setId,
      additionalPicture: additionalPictureToSave,
      userId: this.currentUser.id
    });

    this.eventArticleService
      .saveEventMaxAdditionalsPicture(request)
      .subscribe((res) => {
        if (res.success == 1) {
          this.close();
          this.loading = false;
          this.mdDialog.open(SuccessComponent, {
            data: { message: "Additionals picture have been updated." },
          });
        } else {
          this.loading = false;
        }
      });
  }

  close() {
    this.dialogRef.close();
  }
}



// WEBPACK FOOTER //
// ./src/app/modals/event-max-multilingual-modal/event-max-multilingual-modal.component.ts